import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import {
  MAX_FILE_SIZE,
  useUploadDocument,
  type DocumentRow,
} from '@/hooks/useDocuments';
import { resolveFileMimeType } from '@/utils/fileMime';

export interface JournalAttachmentRow {
  id: string;
  clinic_id: string;
  journal_entry_id: string;
  document_id: string;
  created_by: string | null;
  created_at: string;
  document: DocumentRow;
}

export const JOURNAL_ALLOWED_MIME_TYPES = [
  'application/pdf',
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/webp',
  'video/mp4',
  'video/quicktime',
  'video/webm',
  'video/x-msvideo',
] as const;

export function resolveJournalFileMimeType(file: File): string {
  return resolveFileMimeType(file);
}

export function validateJournalAttachmentFile(file: File): string | null {
  if (file.size > MAX_FILE_SIZE) return 'size_limit_exceeded';
  const mime = resolveJournalFileMimeType(file);
  if (!JOURNAL_ALLOWED_MIME_TYPES.includes(mime as (typeof JOURNAL_ALLOWED_MIME_TYPES)[number])) {
    return 'invalid_type';
  }
  return null;
}

export const journalAttachmentsKey = (entryId: string | null | undefined) =>
  ['journal', 'attachments', entryId ?? null] as const;

export function useJournalEntryAttachments(entryId: string | null | undefined) {
  return useQuery({
    queryKey: journalAttachmentsKey(entryId),
    enabled: Boolean(entryId),
    queryFn: async (): Promise<JournalAttachmentRow[]> => {
      if (!entryId) return [];
      const { data, error } = await supabase
        .from('journal_entry_attachments')
        .select(
          'id, clinic_id, journal_entry_id, document_id, created_by, created_at, document:documents(*)',
        )
        .eq('journal_entry_id', entryId)
        .order('created_at', { ascending: true });
      if (error) throw error;

      return ((data ?? []) as unknown as Array<Omit<JournalAttachmentRow, 'document'> & { document: DocumentRow }>).map(
        (row) => ({
          id: row.id,
          clinic_id: row.clinic_id,
          journal_entry_id: row.journal_entry_id,
          document_id: row.document_id,
          created_by: row.created_by,
          created_at: row.created_at,
          document: row.document,
        }),
      );
    },
    staleTime: 10_000,
  });
}

export function useAttachJournalEntryDocument(clinicId: string | null | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { journal_entry_id: string; document_id: string }) => {
      const { data, error } = await supabase.rpc('attach_journal_entry_document', {
        p_journal_entry_id: input.journal_entry_id,
        p_document_id: input.document_id,
      });
      if (error) throw error;
      return data;
    },
    onSuccess: (_data, input) => {
      qc.invalidateQueries({ queryKey: journalAttachmentsKey(input.journal_entry_id) });
      if (clinicId) {
        qc.invalidateQueries({ queryKey: ['documents', 'patient'] });
      }
    },
  });
}

export function useDetachJournalEntryDocument(clinicId: string | null | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { attachment_id: string; journal_entry_id: string }) => {
      const { data, error } = await supabase.rpc('detach_journal_entry_document', {
        p_attachment_id: input.attachment_id,
      });
      if (error) throw error;
      return data;
    },
    onSuccess: (_data, input) => {
      qc.invalidateQueries({ queryKey: journalAttachmentsKey(input.journal_entry_id) });
      if (clinicId) {
        qc.invalidateQueries({ queryKey: ['documents'] });
      }
    },
  });
}

export function useUploadJournalAttachment(clinicId: string | null | undefined) {
  const uploadDocument = useUploadDocument();
  const attachDocument = useAttachJournalEntryDocument(clinicId);

  return useMutation({
    mutationFn: async (input: {
      clinicId: string;
      patientId: string;
      journalEntryId: string;
      file: File;
    }): Promise<DocumentRow> => {
      const doc = await uploadDocument.mutateAsync({
        clinicId: input.clinicId,
        patientId: input.patientId,
        file: input.file,
        tags: ['journal'],
      });
      await attachDocument.mutateAsync({
        journal_entry_id: input.journalEntryId,
        document_id: doc.id,
      });
      return doc;
    },
  });
}
